/**
 * A/V sync lab, route `#/dev/av` (W3-07; WORKPACKAGES Appendix C H4 "A/V sync within 5 ms after calibration on
 * reference hardware, incl. Bluetooth-like latency; media-element (keep pitch) path within its labelled accuracy").
 * A tool for the person running the check, with a log they hand back:
 *
 *  - Click and flash: a generated metronome (120 BPM) plays through the app's audio engine while a square flashes
 *    on every beat at the engine's visual music time. Filmed with a phone in slow motion (or a photodiode and a
 *    microphone), the gap between flash and click is the remaining A/V error. The Web Audio path and the
 *    media-element path (pitch-preserving rate, used at rates other than 1x) can be compared; each flash is logged
 *    with the engine's music time and the time it was drawn.
 *  - Tap along: the engine's calibration session (16 taps after a count-in) measures the residual latency the user
 *    hears; logged next to the browser's reported output latency.
 *  - Decoder check: audio files of the same metronome (WAV, MP3, OGG, FLAC, M4A, Opus) are decoded by the browser;
 *    the first click of each is located and the MP3 gapless tag read. An MP3 without a LAME tag starts about 25 ms
 *    late (1105 samples at 44.1 kHz) in every decoder; with the tag, browsers drop the delay.
 *  - The log (JSON: browser, devices, settings and every measurement) can be copied or saved.
 */

import { useEffect, useRef, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { readMp3Gapless } from '../../audio/containerInfo';
import { clickTrack, CLICK_SAMPLE_RATE } from '../../core/audio-dsp/clickTrack';
import { encodeWav } from '../../core/audio-dsp/wav';
import type { AssetId } from '../../core/model/types';
import { Button, Icon, Segmented } from '../../ui';
import { m } from './timing.messages';
import { useText } from './timingHooks';
import s from './Timing.module.css';

/** The lab's metronome: 120 BPM, beat 0 at 0.5 s, 60 s long. */
export const LAB_BPM = 120;
export const LAB_FIRST_BEAT_SEC = 0.5;
const LAB_LENGTH_SEC = 60;
const LAB_ASSET = 'dev-av-metronome' as AssetId;
/** How long the square stays lit after a beat (s). */
const FLASH_SEC = 0.06;

export type LabPath = 'webAudio' | 'mediaElement';

export interface LabEntry {
  readonly at: string;
  readonly kind: string;
  readonly data: Readonly<Record<string, unknown>>;
}

/** Index of the beat whose flash window holds music time `t` (null outside every window). */
export function beatAt(t: number, bpm = LAB_BPM, first = LAB_FIRST_BEAT_SEC, flashSec = FLASH_SEC): number | null {
  const period = 60 / bpm;
  if (t < first) return null;
  const k = Math.floor((t - first) / period);
  return t - (first + k * period) < flashSec ? k : null;
}

/** Time (ms) of the first sample louder than `threshold` after `fromSec`, or null. */
export function firstOnsetMs(samples: Float32Array, sampleRate: number, fromSec = 0, threshold = 0.05): number | null {
  for (let i = Math.max(0, Math.floor(fromSec * sampleRate)); i < samples.length; i++) {
    if (Math.abs(samples[i] ?? 0) > threshold) return (i / sampleRate) * 1000;
  }
  return null;
}

/** The environment a log entry is read against. */
function environment(services: Pick<AppServices, 'audio' | 'settings'>): Record<string, unknown> {
  const ctx = services.audio.context;
  return {
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    contextRate: ctx?.sampleRate ?? null,
    outputLatencyMs: typeof ctx?.outputLatency === 'number' ? ctx.outputLatency * 1000 : null,
    baseLatencyMs: typeof ctx?.baseLatency === 'number' ? ctx.baseLatency * 1000 : null,
    detectedLatencyMs: services.audio.detectedLatencyMs,
    calibrationMs: services.settings.value.audio.latency.calibrationMs,
  };
}

function metronomeWav(): ArrayBuffer {
  const pcm = clickTrack({ lengthSec: LAB_LENGTH_SEC, bpm: LAB_BPM, offsetSec: LAB_FIRST_BEAT_SEC, pad: false });
  const bytes = encodeWav([pcm], CLICK_SAMPLE_RATE, 16);
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function AvLabRoute(props: {
  readonly services: AppServices;
  readonly params: Readonly<Record<string, string>>;
}) {
  const { services } = props;
  const text = useText(services);
  const [path, setPath] = useState<LabPath>('webAudio');
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<readonly LabEntry[]>([]);
  const [lastTap, setLastTap] = useState<number | null>(null);
  const [decoded, setDecoded] = useState<ReadonlyArray<Readonly<Record<string, unknown>>>>([]);
  const square = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);

  const add = (kind: string, data: Readonly<Record<string, unknown>>): void => {
    setLog((l) => [...l, { at: new Date().toISOString(), kind, data }]);
  };

  // Leaving the page stops what it started.
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      const tr = services.audio.transport;
      if (tr.state.playing) tr.stop({ returnToStart: true });
    },
    [services],
  );

  const stop = (): void => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    services.audio.transport.stop({ returnToStart: true });
    services.audio.transport.setRate(1);
    setPlaying(false);
    if (square.current !== null) square.current.dataset.on = 'false';
  };

  const start = async (): Promise<void> => {
    if (services.store !== null) {
      services.ui.toast({ key: m.labCloseChart }, { kind: 'warning' });
      return;
    }
    setBusy(true);
    try {
      await services.audio.ensureContext();
      if (services.audio.music?.asset !== LAB_ASSET) await services.audio.loadMusic(LAB_ASSET, metronomeWav());
      services.audio.setOffset(0, 0, 0);
      services.audio.setPreservePitch(path === 'mediaElement');
      services.audio.transport.setRate(path === 'mediaElement' ? 0.75 : 1);
      add('start', { path, rate: services.audio.transport.state.rate, ...environment(services) });
      await services.audio.transport.play({ from: 0 });
      setPlaying(true);
      let lastBeat: number | null = null;
      const tick = (): void => {
        const t = services.audio.transport.musicTime();
        const k = beatAt(t);
        if (square.current !== null) square.current.dataset.on = k === null ? 'false' : 'true';
        if (k !== null && k !== lastBeat) {
          lastBeat = k;
          // The flash: the music time it was drawn at against the beat's own time.
          const beatSec = LAB_FIRST_BEAT_SEC + (k * 60) / LAB_BPM;
          add('flash', { beat: k, musicSec: t, lateMs: (t - beatSec) * 1000, perfMs: performance.now() });
        }
        frame.current = requestAnimationFrame(tick);
      };
      frame.current = requestAnimationFrame(tick);
    } catch (err) {
      add('error', { where: 'start', error: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const tapAlong = async (): Promise<void> => {
    if (playing) stop();
    setBusy(true);
    try {
      await services.audio.ensureContext();
      const ms = await services.audio.calibrate(() => undefined, 16);
      setLastTap(ms);
      add('tapAlong', { residualMs: ms, ...environment(services) });
    } catch (err) {
      add('error', { where: 'tapAlong', error: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const decodeFiles = async (files: readonly File[]): Promise<void> => {
    const rows: Array<Readonly<Record<string, unknown>>> = [];
    for (const f of files) {
      const bytes = new Uint8Array(await f.arrayBuffer());
      const row: Record<string, unknown> = { file: f.name, bytes: bytes.length };
      try {
        const ctx = new OfflineAudioContext(1, 1, 44100);
        const buf = await ctx.decodeAudioData(bytes.slice().buffer);
        row.durationSec = buf.duration;
        row.firstOnsetMs = firstOnsetMs(buf.getChannelData(0), buf.sampleRate, 0.05);
        const gapless = readMp3Gapless(bytes);
        if (gapless !== null) row.mp3 = gapless;
      } catch (err) {
        row.error = err instanceof Error ? err.message : String(err);
      }
      rows.push(row);
    }
    const ref = rows.find((r) => typeof r.file === 'string' && r.file.toLowerCase().endsWith('.wav'));
    const refOnset = typeof ref?.firstOnsetMs === 'number' ? ref.firstOnsetMs : null;
    const withDelta = rows.map((r) =>
      refOnset !== null && typeof r.firstOnsetMs === 'number' ? { ...r, vsWavMs: r.firstOnsetMs - refOnset } : r,
    );
    setDecoded(withDelta);
    add('decoderCheck', { files: withDelta });
  };

  const logText = (): string => JSON.stringify({ format: 'phiedit-av-lab', version: 1, entries: log }, null, 2);

  return (
    <div className={s.lab} data-testid="av-lab">
      <header className={s.labHeader}>
        <Icon name="AudioWaveform" size={20} />
        <h1 className={s.labTitle}>{text.t(m.labTitle)}</h1>
        <Button
          variant="ghost"
          icon="ArrowLeft"
          onClick={() => {
            if (playing) stop();
            services.nav.goLibrary();
          }}
        >
          {text.t(m.labBack)}
        </Button>
      </header>
      <p className={s.help}>{text.t(m.labIntro)}</p>

      <section className={s.labSection} aria-labelledby="lab-flash">
        <h2 id="lab-flash" className={s.labH2}>
          {text.t(m.labFlashTitle)}
        </h2>
        <p className={s.help}>{text.t(m.labFlashHelp)}</p>
        <div className={s.row}>
          <Segmented<LabPath>
            label={text.t(m.labPath)}
            value={path}
            options={[
              { value: 'webAudio', label: text.t(m.labPathWebAudio) },
              { value: 'mediaElement', label: text.t(m.labPathMedia) },
            ]}
            onChange={(v) => {
              if (playing) stop();
              setPath(v);
            }}
          />
          <Button
            variant="primary"
            icon={playing ? 'Square' : 'Play'}
            disabledReason={busy ? { key: m.labBusy } : null}
            onClick={() => {
              if (playing) stop();
              else void start();
            }}
          >
            {text.t(playing ? m.labStop : m.labStart)}
          </Button>
        </div>
        <div ref={square} className={s.labSquare} data-on="false" aria-hidden="true" />
      </section>

      <section className={s.labSection} aria-labelledby="lab-tap">
        <h2 id="lab-tap" className={s.labH2}>
          {text.t(m.labTapTitle)}
        </h2>
        <p className={s.help}>{text.t(m.labTapHelp)}</p>
        <div className={s.row}>
          <Button
            icon="Timer"
            disabledReason={busy ? { key: m.labBusy } : null}
            onClick={() => {
              void tapAlong();
            }}
          >
            {text.t(m.labTapStart)}
          </Button>
          {lastTap !== null && (
            <span className={s.sentence}>{text.t(m.labTapResult, { ms: Math.round(lastTap) })}</span>
          )}
        </div>
      </section>

      <section className={s.labSection} aria-labelledby="lab-decode">
        <h2 id="lab-decode" className={s.labH2}>
          {text.t(m.labDecodeTitle)}
        </h2>
        <p className={s.help}>{text.t(m.labDecodeHelp)}</p>
        <input
          type="file"
          multiple
          accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a,.opus"
          aria-label={text.t(m.labDecodePick)}
          data-testid="av-lab-files"
          onChange={(e) => {
            const files = [...(e.currentTarget.files ?? [])];
            e.currentTarget.value = '';
            if (files.length > 0) void decodeFiles(files);
          }}
        />
        {decoded.length > 0 && (
          <table className={s.table} data-testid="av-lab-decoded">
            <thead>
              <tr>
                <th>{text.t(m.labColFile)}</th>
                <th>{text.t(m.labColOnset)}</th>
                <th>{text.t(m.labColVsWav)}</th>
                <th>{text.t(m.labColMp3)}</th>
              </tr>
            </thead>
            <tbody>
              {decoded.map((r) => (
                <tr key={String(r.file)}>
                  <td>{String(r.file)}</td>
                  <td>
                    {typeof r.firstOnsetMs === 'number'
                      ? r.firstOnsetMs.toFixed(2)
                      : typeof r.error === 'string'
                        ? r.error
                        : '-'}
                  </td>
                  <td>{typeof r.vsWavMs === 'number' ? r.vsWavMs.toFixed(2) : '-'}</td>
                  <td>{r.mp3 === undefined ? '-' : JSON.stringify(r.mp3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className={s.labSection} aria-labelledby="lab-log">
        <h2 id="lab-log" className={s.labH2}>
          {text.t(m.labLogTitle, { count: log.length })}
        </h2>
        <div className={s.row}>
          <Button
            icon="Copy"
            disabledReason={log.length === 0 ? { key: m.labLogEmpty } : null}
            onClick={() => {
              void navigator.clipboard.writeText(logText()).then(
                () => {
                  services.ui.toast({ key: m.labLogCopied }, { kind: 'info' });
                },
                () => undefined,
              );
            }}
          >
            {text.t(m.labLogCopy)}
          </Button>
          <Button
            icon="Download"
            disabledReason={log.length === 0 ? { key: m.labLogEmpty } : null}
            onClick={() => {
              const url = URL.createObjectURL(new Blob([logText()], { type: 'application/json' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `phiedit-av-lab-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
              a.click();
              setTimeout(() => {
                URL.revokeObjectURL(url);
              }, 30_000);
            }}
          >
            {text.t(m.labLogSave)}
          </Button>
          <Button
            variant="ghost"
            icon="Trash2"
            onClick={() => {
              setLog([]);
            }}
          >
            {text.t(m.labLogClear)}
          </Button>
        </div>
      </section>
    </div>
  );
}
